import { useEffect, useState } from "react";
import api, { apiError, maskDoc } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, UserPlus } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import Autocomplete from "@/components/panel/Autocomplete";
import { Button, Field, Input, Select, Textarea } from "@/components/ui-kit";

/* ---------- Cadastro rápido de cliente ---------- */
/** Estava só no PDV; virou parte do picker para que OS, orçamento e aluguel
 *  também consigam cadastrar na hora, sem sair do formulário. */
const ENDERECO_VAZIO = { cep: "", street: "", number: "", complement: "", district: "", city: "", state: "" };

/**
 * Cadastro de cliente sem sair de onde se está.
 *
 * Dois níveis na MESMA tela. O simplificado (nome, telefone, documento) é o
 * caso do balcão com fila: é o mínimo para emitir a nota e achar o cliente
 * depois. O completo abre no mesmo modal quando o atendimento comporta — aluguel,
 * ordem de serviço e entrega precisam de endereço, e mandar o vendedor terminar
 * no CRM significava, na prática, um cadastro pela metade para sempre.
 *
 * O que NÃO muda ao expandir: nada do que já foi digitado se perde, e o botão de
 * salvar continua o mesmo. Expandir é revelar campos, não recomeçar.
 */
function QuickCustomer({ initialName = "", onClose, onCreated }) {
  const [form, setForm] = useState({
    name: initialName, phone: "", document: "",
    email: "", rg: "", birthdate: "", gender: "", notes: "",
  });
  const [completo, setCompleto] = useState(false);
  const [endereco, setEndereco] = useState(ENDERECO_VAZIO);
  const [saving, setSaving] = useState(false);

  const setE = (k, v) => setEndereco((a) => ({ ...a, [k]: v }));

  // Mesmo ViaCEP do checkout da loja. Falha em silêncio: dá para digitar à mão,
  // e o balcão não pode parar porque um serviço externo caiu.
  const lookupCep = async (cepRaw) => {
    if (cepRaw.replace(/\D/g, "").length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepRaw.replace(/\D/g, "")}/json/`);
      const d = await res.json();
      if (d && !d.erro) {
        setEndereco((a) => ({
          ...a,
          street: d.logradouro || a.street, district: d.bairro || a.district,
          city: d.localidade || a.city, state: d.uf || a.state,
        }));
      }
    } catch { /* mantém o que foi digitado */ }
  };

  // Endereço é tudo-ou-nada (o servidor valida igual): só viaja quando os
  // obrigatórios estão lá. Meio endereço não entrega encomenda.
  const enderecoCompleto = ["cep", "street", "number", "district", "city", "state"]
    .every((k) => String(endereco[k] || "").trim());

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/pos/customers", {
        ...form,
        birthdate: form.birthdate || undefined,
        address: completo && enderecoCompleto ? endereco : undefined,
      });
      toast.success("Cliente cadastrado");
      onCreated(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Novo cliente" maxWidth={completo ? "max-w-2xl" : undefined} testId="quick-customer">
      <div className="space-y-3">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" data-testid="qc-name" />
        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefone / WhatsApp" data-testid="qc-phone" />
        <Input value={form.document} onChange={(e) => setForm({ ...form, document: maskDoc(e.target.value) })} placeholder="CPF/CNPJ (opcional)" data-testid="qc-doc" mono />

        {/* A alternância fica ENTRE os campos e o botão: é o próximo passo de
            quem olhou o mínimo e concluiu que precisa de mais. */}
        <button type="button" onClick={() => setCompleto((v) => !v)} data-testid="qc-toggle-full"
          className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700">
          {completo ? <ChevronUp size={15} aria-hidden /> : <ChevronDown size={15} aria-hidden />}
          {completo ? "Usar cadastro simplificado" : "Cadastro completo (endereço, RG, nascimento…)"}
        </button>

        {completo && (
          <div className="space-y-3 border-t border-slate-100 pt-3" data-testid="qc-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-mail" data-testid="qc-email" />
              <Input value={form.rg} onChange={(e) => setForm({ ...form, rg: e.target.value })} placeholder="RG" data-testid="qc-rg" mono />
              <Field label="Nascimento" labelTone="plain">
                <Input type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} data-testid="qc-birthdate" />
              </Field>
              <Field label="Gênero" labelTone="plain">
                <Select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="qc-gender">
                  <option value="">Não informar</option>
                  <option value="feminino">Feminino</option>
                  <option value="masculino">Masculino</option>
                  <option value="outro">Outro</option>
                </Select>
              </Field>
            </div>

            <fieldset className="space-y-3">
              <legend className="text-eyebrow uppercase tracking-wider text-slate-500">Endereço</legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input value={endereco.cep} onChange={(e) => { setE("cep", e.target.value); lookupCep(e.target.value); }}
                  placeholder="CEP" data-testid="qc-cep" mono />
                <Input value={endereco.street} onChange={(e) => setE("street", e.target.value)}
                  placeholder="Rua" data-testid="qc-street" className="sm:col-span-2" />
                <Input value={endereco.number} onChange={(e) => setE("number", e.target.value)} placeholder="Número" data-testid="qc-number" />
                <Input value={endereco.complement} onChange={(e) => setE("complement", e.target.value)} placeholder="Complemento" data-testid="qc-complement" />
                <Input value={endereco.district} onChange={(e) => setE("district", e.target.value)} placeholder="Bairro" data-testid="qc-district" />
                <Input value={endereco.city} onChange={(e) => setE("city", e.target.value)} placeholder="Cidade" data-testid="qc-city" className="sm:col-span-2" />
                <Input value={endereco.state} onChange={(e) => setE("state", e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="UF" data-testid="qc-state" mono />
              </div>
              {/* Diz o que vai acontecer ANTES de salvar. Sem isto, o endereço
                  pela metade era descartado em silêncio e o vendedor só
                  descobria na hora de entregar. */}
              {!enderecoCompleto && Object.values(endereco).some(Boolean) && (
                <p className="text-xs text-amber-700" data-testid="qc-address-partial">
                  Endereço incompleto — falta CEP, rua, número, bairro, cidade ou UF. O cliente é cadastrado sem endereço.
                </p>
              )}
            </fieldset>

            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              placeholder="Observações (condição, preferência de atendimento…)" data-testid="qc-notes" />
          </div>
        )}

        <Button onClick={save} loading={saving} size="lg" block data-testid="qc-save">
          {saving ? "Salvando..." : "Cadastrar e vincular"}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Escolha de cliente cadastrado, com cadastro rápido embutido.
 *
 * A busca é a mesma do PDV (`/pos/customers/search`, mínimo de 2 letras) e
 * devolve o cliente inteiro — os formulários costumam preencher telefone e
 * documento a partir dele.
 *
 * A falha de busca aparece na tela: antes o erro era engolido com `catch(() => {})`
 * e uma busca quebrada (sessão expirada, rede fora) se passava por "nada encontrado".
 */
/**
 * Valor de `value` quando o nome foi digitado à mão em vez de escolhido no
 * cadastro. Existe porque o `Autocomplete` só mostra o rótulo quando há valor.
 */
export const FREE_TEXT = "__avulso__";

export default function CustomerPicker({
  value,
  onChange,
  selectedLabel,
  placeholder = "Buscar cliente cadastrado...",
  allowCreate = true,
  // Balcão atende quem não tem cadastro: com isto a lista oferece "usar o que
  // foi digitado", em vez de obrigar a cadastrar antes de abrir a OS.
  allowFreeText = false,
  testId,
  width = "w-full",
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setItems([]); setError(""); return; }
    const t = setTimeout(() => {
      api.get("/pos/customers/search", { params: { q } })
        .then((r) => { setItems(r.data || []); setError(""); })
        .catch((e) => { setItems([]); setError(apiError(e)); });
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const typed = query.trim();

  const options = items.map((c) => ({
    value: c.id,
    label: c.document ? `${c.name} · ${maskDoc(c.document)}` : (c.phone ? `${c.name} · ${c.phone}` : c.name),
  }));
  if (allowFreeText && typed.length >= 2 && !items.some((c) => c.name.toLowerCase() === typed.toLowerCase())) {
    options.unshift({
      value: FREE_TEXT,
      label: `Usar "${typed}" (cliente avulso)`,
      // Sem isto o campo fechado exibiria a frase da ação em vez do nome escolhido.
      displayLabel: selectedLabel || typed,
    });
  }

  const pick = (id) => {
    if (id === FREE_TEXT) { onChange({ id: null, name: typed }); return; }
    onChange(items.find((c) => c.id === id) || null);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Autocomplete
          value={value || ""}
          onChange={pick}
          options={options}
          query={query}
          onQueryChange={setQuery}
          selectedLabel={selectedLabel}
          placeholder={placeholder}
          emptyLabel="— Sem cliente vinculado"
          testId={testId}
          width={width}
        />
        {allowCreate && (
          <button type="button" onClick={() => setCreating(true)} title="Cadastrar novo cliente"
            data-testid={testId ? `${testId}-new` : undefined}
            className="shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg">
            <UserPlus size={16} />
          </button>
        )}
      </div>

      {query.trim().length === 1 && <p className="text-xs text-slate-400">Digite ao menos 2 letras para buscar.</p>}
      {error && <p className="text-xs text-red-500" data-testid={testId ? `${testId}-error` : undefined}>{error}</p>}

      {creating && (
        <QuickCustomer
          initialName={query.trim()}
          onClose={() => setCreating(false)}
          onCreated={(c) => { setCreating(false); setItems([c]); onChange(c); }}
        />
      )}
    </div>
  );
}
